import { makeStyles, Paper, Table, TableBody, TableCell, TableHead, TableRow, Theme } from '@material-ui/core';
import { BaseListCompField, BaseListCompFieldsSet, BaseListCompFieldsSettings, BaseListCompProps, createHeaderColumn, createPreviewItemCell,
    createRemoveItemCell, getNumberFormatter, useModePanelStyle, useModeTableStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useMemo } from 'react';
import { findSelectedEntityNode } from '.';
import { EntitiesListingDataItem } from '..';
import { EntityNode } from '../../containers';
import { ReadonlyNonEmptyArray } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {

        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        tableContainer: {
            overflow: 'auto',
            flex    : 1,
        },

        tableCol: {
            textAlign     : 'left',
            '&.homeId-col': {
                textAlign: 'center',
            },
            '&.description-col': {
                textAlign : 'left',
                width     : '100%',
                whiteSpace: 'normal',
            },
            '&.icon-col, &.image-col': {
                textAlign: 'center',
                '& img'  : {
                    height   : 75,
                    width    : 100,
                    objectFit: 'cover',
                    boxShadow: theme.shadows[2],
                },
            },
            '&.spacer-col': {
                width  : '100%',
                padding: 0,
            },
        },

        emptyMessage: {
            flex     : 1,
            alignSelf: 'center',
        },
    };
}, { name: 'EntitiesListingTableViewComp', index: 1 });


// Because this table display entity's basic attributes, custom attributes, and descendant counts and it is possible that they have the same name.
// For example, it is possible for a CUSTOM Attribute to have a name call "entityClass". "entityClass" is already used for the entity's BASIC
// attribute therefore we will have a naming conflict. To fix this, we will prepend a string "attributes" to the attribute name when the
// value we want to display is an attribute.
// For example:
//      - To create a table column to display a custom attribute call "serial_number", set the column field name to "attributes.serial_number"
//      - To create a table column to display a descendant count for entity class "driver", set the column field name to "descendant_counts.driver"
//      - To create a table column to display the entity's "homeId", set the column field name to "homeId", don't need prefix.
export const ENTITY_CUSTOM_ATTRIBUTES_COLUMN_PREFIX = 'attributes';
export const ENTITY_DESCENDANT_COUNTS_COLUMN_PREFIX = 'descendant_counts';



export interface TimeSeriesTableCompField extends BaseListCompField {
    readonly decimalPlaces?: number | undefined;
}


export interface EntitiesListingTableFieldsSet extends BaseListCompFieldsSet {
    readonly entityId?: BaseListCompField | undefined;
    readonly name?: BaseListCompField | undefined;
    readonly description?: BaseListCompField | undefined;
    readonly parentId?: BaseListCompField | undefined;
    readonly entityClass?: BaseListCompField | undefined;
    readonly homeId?: BaseListCompField | undefined;
    readonly icon?: BaseListCompField | undefined;
    readonly image?: BaseListCompField | undefined;
    readonly updatedAt?: BaseListCompField | undefined;
    readonly createdAt?: BaseListCompField | undefined;

    // Dynamic columns based on entity attributes
    readonly [attributeName: string]: TimeSeriesTableCompField | undefined;
}



/**
 * The props required by the EntitiesListingTableViewComp components.
 */
export interface EntitiesListingTableViewCompProps extends BaseListCompProps<EntityNode> {

    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // The array of entities data items.
    readonly listData: ReadonlyNonEmptyArray<EntitiesListingDataItem>;

    // The table columns settings
    readonly fieldsSettings: BaseListCompFieldsSettings<EntitiesListingTableFieldsSet>;

    readonly locale?: string | undefined;

    // Decimal places to apply to all metrics
    readonly decimalPlaces?: number | undefined;

    // The entityId of the current selected entity.
    readonly selectedEntityId?: string | undefined;

    // The "listData" contains all the data and by default, the this component will display all children. If these 2 props are provided,
    // this component will only display items from the listData that are in the currentPage/itemsPerPage range.
    readonly currentPage?: number | undefined;
    readonly itemsPerPage?: number | undefined;

    readonly texts: {
        readonly loadMoreLabel: string;
    }

    // The handler for when the user select an entity
    readonly onEntitySelected?: ((entityNode: EntityNode)=> void) | undefined;

    // The handler for when the user click on the preview button
    readonly onPreviewItemClicked?: (entityNode: EntityNode)=> void;


    // The entity to use as current root. When in TABLE view, the user can select any entity as root. This is similar to how file browsers
    // work which let the user to enter a sub directory to view the directory's contents. So if the user select a different entity as root,
    // we will show only the children of the subRootEntityId
    readonly subRootEntityId?: string | undefined;

    // The handler for when the user select another entity as root
    readonly onChangeRootEntity?: ((entityNode: EntityNode)=> void) | undefined;
}


/**
 * This is a component that display list of entities using Table component.
 */
export const EntitiesListingTableViewComp: React.FC<EntitiesListingTableViewCompProps> = (props: EntitiesListingTableViewCompProps) => {
    const classes = useStyle();
    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();


    const defaultNumberFormatter = useMemo(() => {
        return getNumberFormatter(props.locale ?? 'en', props.decimalPlaces ?? 3);
    }, [props.decimalPlaces, props.locale]);


    // Because Intl.NumberFormatter can only round number to 0 and 20 decimalPlaces therefore we can pre-create number formatter ahead of time
    const numberFormatterByDecimalPlaces = useMemo(() => {
        const result: Intl.NumberFormat[] = [];
        for (let i = 0; i <= 20; i += 1) {
            result[i] = getNumberFormatter(props.locale ?? 'en', i);
        }
        return result;
    }, [props.locale]);


    const rootEntityNode = useMemo(() => {
        return props.listData[0];
    }, [props.listData]);


    /**
     * If subRootEntityId is provided, find the EntityNode that contain the entity with id subRootEntityId
     */
    const subRootEntityNode = useMemo(() => {
        if (props.subRootEntityId) {
            return findSelectedEntityNode(props.subRootEntityId, props.listData[0]);
        }
        return undefined;
    }, [props.subRootEntityId, props.listData]);


    /**
     * Get all the root entity's children EXCLUDING the "Load More" dummy node that was added to the children array
     */
    const children = useMemo(() => {
        return (subRootEntityNode ?? rootEntityNode).displayValue.children?.filter((dataItem) => {
            // Filter out dummy nodes that is used for Load More
            return !dataItem.isLoadMoreItem;
        });
    }, [rootEntityNode, subRootEntityNode]);


    /**
     * children array includes all entities belonged to the root entities. Because we want to display them 1 page at a time,
     * we need to filter them based on the currentPage and pageSize
     */
    const currentPageChildren = useMemo((): readonly EntitiesListingDataItem[] | undefined => {
        if (children && props.currentPage !== undefined && props.itemsPerPage !== undefined) {
            // If currentPage and itemsPerPage are provided then we will only take the items from the "children" array that are within the
            // currentPage/itemsPerPage range. These will be the items that we will display.
            return children.slice(props.currentPage * props.itemsPerPage, (props.currentPage + 1) * props.itemsPerPage);
        }

        // Else, return all the children
        return children;
    }, [children, props.currentPage, props.itemsPerPage]);


    return (
        <div>
            <Paper
                elevation={2}
                className={clsx(props.className, classes.root, panelClasses.root, props.className)}
            >
                <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                    {children && children.length > 0 && (
                        <div className={classes.tableContainer}>
                            <Table className={tableClasses.root} size="medium">
                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        {/* filter out hidden columns first and then go through each column to create Header Cell */}
                                        {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                            return !props.fieldsSettings.fields[fieldName]?.hidden;
                                        }).map((fieldName: string) => {
                                            const field = props.fieldsSettings.fields[fieldName];
                                            if (field && !field.hidden) {
                                                return createHeaderColumn(props.fieldsSettings, fieldName, field,
                                                    `${tableClasses.tableCol} ${classes.tableCol} ${fieldName}-col`);
                                            }
                                            return <></>;
                                        })}
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'spacer-col')} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentPageChildren && currentPageChildren.map((dataItem): JSX.Element => {
                                        return (
                                            <TableRow
                                                className={clsx(
                                                    tableClasses.tableBodyRow, 'clickable', dataItem.selected && 'selected',
                                                )}
                                                key={dataItem.actualValue.entity.entityId}
                                                onClick={() => {
                                                    if (props.onChangeRootEntity) {
                                                        props.onChangeRootEntity(dataItem.actualValue);
                                                    }
                                                }}
                                            >
                                                {/* filter out hidden columns first and then go through each column to create Table Cell */}
                                                {Object.entries(props.fieldsSettings.fields).filter(([fieldName, field]) => {
                                                    return !props.fieldsSettings.fields[fieldName]?.hidden;
                                                }).map(([fieldName, field]) => {

                                                    // Implement custom TableCell for Icon and Image
                                                    if (fieldName === 'icon' && props.fieldsSettings.fields.icon) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`)}
                                                            >
                                                                {dataItem.displayValue.icon && <img src={dataItem.displayValue.icon} alt="icon" />}
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (fieldName === 'image' && props.fieldsSettings.fields.image) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`)}
                                                            >
                                                                {dataItem.displayValue.image && <img src={dataItem.displayValue.image} alt="icon" />}
                                                            </TableCell>
                                                        );
                                                    }

                                                    if (fieldName === 'remove' && props.fieldsSettings.fields.remove) {
                                                        return (
                                                            // NOTE: dataItem.canBeRemoved must be true
                                                            createRemoveItemCell(dataItem, clsx(
                                                                tableClasses.tableCol, classes.tableCol, `${fieldName}-col`,
                                                            ),
                                                            props.onRemoveItemClicked)
                                                        );
                                                    }

                                                    if (fieldName === 'preview' && props.fieldsSettings.fields.preview) {
                                                        return createPreviewItemCell(
                                                            dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`),
                                                            props.onPreviewItemClicked,
                                                        );
                                                    }


                                                    /**
                                                     * Handle columns for displaying value for BASIC attributes e.g. "entityId", "entityClass",
                                                     * "homeId", CUSTOM attributes e.g. "serial_number", "address", etc..., and DESCENDANT_COUNTS
                                                     * e.g. "feeder", "line", etc...
                                                     */
                                                    if (props.fieldsSettings.fields[fieldName]) {

                                                        // Get the value from the "dataItem" based on the fieldName
                                                        const fieldValue = (() => {

                                                            // For CUSTOM ATTRIBUTES and DESCENDANT_COUNTS, we know we prepended the prefix to the
                                                            // table field name. So if the field name starts with these prefixes, we know they
                                                            // are the table columns for displaying custom attributes or descendant counts.
                                                            const isAttributeField = fieldName.startsWith(ENTITY_CUSTOM_ATTRIBUTES_COLUMN_PREFIX);
                                                            const isDesCountsField = fieldName.startsWith(ENTITY_DESCENDANT_COUNTS_COLUMN_PREFIX);

                                                            if (isAttributeField || isDesCountsField) {
                                                                // Split the fieldName and get the second part of the fieldName which should be the
                                                                // attribute's key or descendant's name
                                                                const dataName = fieldName.split('.')[1];

                                                                // If column is for showing attributes then get data from the entity's "attributes"
                                                                if (dataName && isAttributeField) {
                                                                    return dataItem.displayValue.attributes?.[dataName]
                                                                            ?? dataItem.actualValue.entity.attributes?.[dataName];
                                                                }

                                                                // If column is for showing descendant counts then get data from the entity's
                                                                // "descendantCounts"
                                                                if (dataName && isDesCountsField) {
                                                                    return dataItem.actualValue.entity.descendantCounts?.[dataName];
                                                                }
                                                            }
                                                            return dataItem.displayValue[fieldName] ?? dataItem.actualValue.entity[fieldName];
                                                        })();

                                                        const formattedValue = (() => {
                                                            if (typeof fieldValue === 'number') {
                                                                if (field && field.decimalPlaces !== undefined) {
                                                                    // Make sure the decimalPlaces is between 0 and 20
                                                                    const decimalPlaces = Math.max(0, Math.min(20, field.decimalPlaces));
                                                                    return numberFormatterByDecimalPlaces[decimalPlaces].format(fieldValue);
                                                                }
                                                                // Use default number formatter
                                                                return defaultNumberFormatter.format(fieldValue);
                                                            }
                                                            return fieldValue;
                                                        })();


                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`)}
                                                            >
                                                                { formattedValue ?? '' }
                                                            </TableCell>
                                                        );
                                                    }

                                                    return <></>;
                                                })}
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'spacer-col')} />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            {(!currentPageChildren || currentPageChildren.length === 0) && props.emptyDataMessageComp}
                        </div>
                    )}

                    {children && children.length > 0 && props.paginationComp }

                    {(!children || children.length === 0) && (
                        <div className={classes.emptyMessage}>
                            {props.noDataMessageComp}
                        </div>
                    )}
                </div>
            </Paper>
        </div>
    );
};
